import React from 'react';
import { InputCheckbox, ScreenReaderContent } from '@companycam/slab-web';
import styled, { css } from 'styled-components';

import {
  MultiselectPickerItemComponentProps,
  SelectableItem,
  UseMultiselectReturn,
} from 'components/shared/forms/MultiselectPicker';

type ItemOverlayProps<TItem extends SelectableItem> = {
  multiselect: UseMultiselectReturn;
  label: string;
  item: TItem;
  ItemComponent: React.ComponentType<
    MultiselectPickerItemComponentProps<TItem>
  >;
  onItemOverlayClicked?: (item: TItem) => void;
};

const Overlay = styled.span<{ isChecked: boolean }>`
  display: block;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  padding: var(--cc_size_spacing_s);
  pointer-events: none;
  border-radius: var(--cc_size_border_radius_l);
  ${(props) =>
    props.isChecked
      ? css`
          opacity: 1;
          background: var(--cc_color_background_backdrop_alpha_50);
        `
      : css`
          opacity: 0;
          background: var(--cc_color_background_backdrop_alpha_70);
        `}
`;

const Container = styled.div`
  position: relative;
  aspect-ratio: 1;
  display: grid;
  gap: 3%;
  cursor: pointer;
  border-radius: var(--cc_size_border_radius_l);

  &:hover ${Overlay} {
    transition: all ease-in-out 150ms;
    opacity: 1;
    background: var(--cc_color_background_backdrop_alpha_70);
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  justify-self: flex-start;
`;

export const ItemOverlay = <TItem extends SelectableItem>({
  multiselect,
  item,
  ItemComponent,
  label,
  onItemOverlayClicked,
}: ItemOverlayProps<TItem>) => {
  const isChecked = multiselect.checkSelected(item);

  const onToggleItem = () => {
    multiselect.toggleSelected(item);
  };

  const onItemClicked = () => {
    if (!onItemOverlayClicked) {
      onToggleItem();
      return;
    }

    onItemOverlayClicked(item);
  };

  return (
    <Container>
      <div onClick={onItemClicked}>
        <ItemComponent item={item} />
      </div>
      <Overlay isChecked={isChecked}>
        <CheckboxContainer>
          <InputCheckbox
            design="assetFeed"
            aria-checked={isChecked}
            id={`item-${item.id}`}
            checked={isChecked}
            onChange={onToggleItem}
            label={<ScreenReaderContent>{label}</ScreenReaderContent>}
            value={label}
          />
        </CheckboxContainer>
      </Overlay>
    </Container>
  );
};
